import React, { useEffect, useState } from "react";
import { Button, PerexBox } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AdPreview } from "../../elements/AdPreview";
import { NextIcon } from "../../elements/Icons";
import { Preview as ImageAssetLinesPreview } from "../../elements/ImageAssetLines/Preview";
import GoogleWizardHeader from "../../elements/MetaWizard/GoogleWizardHeader";
import { UpdateMonthlyBudget } from "../../elements/MonthlyBudget/update";
import { ProgressStepper, StepsT } from "../../elements/ProgressStepper";
import { SmallLoader } from "../../elements/SmallLoader";
import { Preview as TextAssetLinesPreview } from "../../elements/TextAssetLines/Preview";
import WizardHeader from "../../elements/WizardHeader";
import {
  AdSystemT,
  AssetLineKindT,
  ImageSuggestionKindT,
  SuggestionKindT,
  WizardStepT,
  useActiveImagesAssetLinesQuery,
  useActiveTextsAssetLinesQuery,
  useOrganizationDataQuery,
  useProductExclusionQuery,
} from "../../graphql/generated/graphql";
import { useSetWizardStepEffect } from "./useSetWizardStepEffect";

const calculatePercent = (count: number, total: number) => Math.round(total > 0 ? (count / total) * 100 : 0);

type CheckPropsT = {
  hasMetaCampaignsEnabled: boolean;
};

export const Check = ({ hasMetaCampaignsEnabled }: CheckPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Wizard Check Page" });
  useSetWizardStepEffect({ wizardStep: WizardStepT.CheckT });
  const [assetsAreValid, setAssetsAreValid] = useState(false);

  const navigate = useNavigate();

  const { data: organizationData, loading: loadingProductsAndDesign } = useOrganizationDataQuery();
  const { data: productExclusionData, loading: loadingProductFlags } = useProductExclusionQuery({
    variables: { adSystem: AdSystemT.GoogleAdsT },
  });
  const { data: dataActiveImageAssetLines, loading: loadingImages } = useActiveImagesAssetLinesQuery({
    fetchPolicy: "network-only",
  });
  const { data: dataActiveTextAssetLines, loading: loadingTexts } = useActiveTextsAssetLinesQuery({
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!loadingImages && !loadingTexts) {
      let assetsValid = false;
      let imagesValid = false;
      let textsValid = false;
      const imageAssets = dataActiveImageAssetLines?.organization?.activeImagesAssetLines || [];
      const textAssets = dataActiveTextAssetLines?.organization?.activeTextsAssetLines || [];

      const squareBanners = imageAssets.filter((line) => line?.kind === AssetLineKindT.SquareMarketingImageT);
      const marketingBanners = imageAssets.filter((line) => line?.kind === AssetLineKindT.MarketingImageT);
      const shortHeadlines = textAssets.filter((line) => line?.kind === AssetLineKindT.ShortHeadlineT) || [];
      const longHeadlines = textAssets.filter((line) => line?.kind === AssetLineKindT.LongHeadlineT) || [];
      const shortDescriptions = textAssets.filter((line) => line?.kind === AssetLineKindT.ShortDescriptionT) || [];
      const longDescriptions = textAssets.filter((line) => line?.kind === AssetLineKindT.LongDescriptionT) || [];

      if (squareBanners.length > 0 && marketingBanners.length > 0) {
        imagesValid = true;
      } else {
        navigate("/wizard/image-selection");
      }

      if (
        shortHeadlines.length > 2 &&
        longHeadlines.length > 0 &&
        shortDescriptions.length > 0 &&
        longDescriptions.length > 0
      ) {
        textsValid = true;
      } else {
        navigate("/wizard/text-selection");
      }

      if (imagesValid && textsValid) {
        assetsValid = true;
      }

      setAssetsAreValid(assetsValid);
    }
  }, [loadingImages, loadingTexts, dataActiveImageAssetLines, dataActiveTextAssetLines, navigate]);

  const excludedProductFlag = productExclusionData?.organization?.productFlags.find(
    (productFlag) => productFlag.excludedGoogleAds
  );
  const excludedCategories = (productExclusionData?.organization?.categories || []).filter(
    (category) => category.excludedGoogleAds
  );
  const excludedCategoryGuids = excludedCategories.map((category) => category.guid);
  const excludedCategoriesOnlyParents = excludedCategories.filter(
    (category) => excludedCategoryGuids.indexOf(category.parentGuid) === -1
  );

  const productsCount = productExclusionData?.organization?.selectableProductsCount || 0;
  const productsPercent = calculatePercent(productsCount, productExclusionData?.organization?.productsCount || 0);

  return (
    <div>
      {hasMetaCampaignsEnabled && <WizardHeader />}
      <GoogleWizardHeader />
      <ProgressStepper currentStep={StepsT.STEP_CHECK} />
      <PerexBox>{t("InfoBox")}</PerexBox>
      <h2>{t("Products Headline")}</h2>
      <div
        style={{
          border: "1px solid var(--app-border)",
          borderRadius: "var(--app-radius-box)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: "267px",
          position: "relative",
          padding: "20px",
          margin: "40px 0",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-10px",
            left: "50%",
            transform: "translateX(-50%)",
            color: "var(--app-text)",
            background: "var(--app-surface)",
            padding: "0px 10px",
          }}
        >
          {t("Products BoxLabel")}
        </div>
        {loadingProductFlags ? (
          <SmallLoader />
        ) : (
          <>
            <span>
              <Trans
                components={{ strong: <strong style={{ color: "var(--app-primary)" }} /> }}
                i18nKey="Products Text"
                t={t}
                values={{ productsCount, productsPercent }}
              />
              {excludedProductFlag && (
                <>
                  <br />
                  <Trans
                    components={{ strong: <strong style={{ color: "var(--app-primary)" }} /> }}
                    i18nKey="Products without flag"
                    t={t}
                    values={{ flag: excludedProductFlag.name }}
                  />
                </>
              )}
              {excludedCategoriesOnlyParents.length > 0 && (
                <>
                  <br />
                  <Trans
                    components={{ strong: <strong style={{ color: "var(--app-primary)" }} /> }}
                    i18nKey="Products in categories"
                    t={t}
                    values={{ categories: excludedCategoriesOnlyParents.map((category) => category.path).join(", ") }}
                  />
                </>
              )}
            </span>
          </>
        )}
      </div>
      <h2>{t("Budget Headline")}</h2>
      <div style={{ margin: "40px 0" }}>
        <UpdateMonthlyBudget />
      </div>
      <div
        style={{
          background: "var(--app-surface-muted)",
          borderRadius: "var(--app-radius-box)",
          padding: "10px",
          margin: "20px 0",
        }}
      >
        <h2>{t("Preview Headline")}</h2>
        {loadingImages || loadingTexts || loadingProductsAndDesign ? (
          <SmallLoader />
        ) : (
          <AdPreview
            activeImagesAssetLines={dataActiveImageAssetLines?.organization?.activeImagesAssetLines || []}
            activeTextAssetLines={dataActiveTextAssetLines?.organization?.activeTextsAssetLines || []}
            organization={organizationData?.organization}
          />
        )}
      </div>
      <h2>{t("SquareMarketingImage")}</h2>
      {loadingImages && (
        <div>
          <SmallLoader />
        </div>
      )}
      <ImageAssetLinesPreview
        activeImagesAssetLines={dataActiveImageAssetLines?.organization?.activeImagesAssetLines || []}
        kind={ImageSuggestionKindT.SquareMarketingImageT}
      />
      <h2>{t("MarketingImage")}</h2>
      {loadingImages && (
        <div>
          <SmallLoader />
        </div>
      )}
      <ImageAssetLinesPreview
        activeImagesAssetLines={dataActiveImageAssetLines?.organization?.activeImagesAssetLines || []}
        kind={ImageSuggestionKindT.MarketingImageT}
      />
      <h2>{t("ShortHeadline")}</h2>
      {loadingTexts && (
        <div>
          <SmallLoader />
        </div>
      )}
      <TextAssetLinesPreview
        activeTextAssetLines={dataActiveTextAssetLines?.organization?.activeTextsAssetLines || []}
        kind={SuggestionKindT.ShortHeadlineT}
      />
      <h2>{t("LongHeadline")}</h2>
      {loadingTexts && (
        <div>
          <SmallLoader />
        </div>
      )}
      <TextAssetLinesPreview
        activeTextAssetLines={dataActiveTextAssetLines?.organization?.activeTextsAssetLines || []}
        kind={SuggestionKindT.LongHeadlineT}
      />
      <h2>{t("ShortDescription")}</h2>
      {loadingTexts && (
        <div>
          <SmallLoader />
        </div>
      )}
      <TextAssetLinesPreview
        activeTextAssetLines={dataActiveTextAssetLines?.organization?.activeTextsAssetLines || []}
        kind={SuggestionKindT.ShortDescriptionT}
      />
      <h2>{t("LongDescription")}</h2>
      {loadingTexts && (
        <div>
          <SmallLoader />
        </div>
      )}
      <TextAssetLinesPreview
        activeTextAssetLines={dataActiveTextAssetLines?.organization?.activeTextsAssetLines || []}
        kind={SuggestionKindT.LongDescriptionT}
      />
      <br />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ marginRight: "10px" }}>
          <Button data-test-id="edit-assets" variant="secondary" onClick={() => navigate("/wizard/image-selection")}>
            <span style={{ display: "flex", alignItems: "center" }}>
              <span>{t("Back to edit")}</span>
            </span>
          </Button>
        </div>

        <Button
          data-test-id="continue-to-next-step"
          disabled={!assetsAreValid}
          onClick={() => navigate("/wizard/finish")}
        >
          <span style={{ display: "flex", alignItems: "center" }}>
            {t("Start campaign")}
            &nbsp;
            <NextIcon />
          </span>
        </Button>
      </div>
    </div>
  );
};
